import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectUsers, selectUsersLoading, selectUsersError, selectUsersMessage } from '../../features/users/userSlice';
import UserListSkeleton from './UserListSkeleton';
import { useListFilters } from '../../hooks/useListFilters';
import OmButton from '../ui/OmButton';
import OmTextField from '../ui/OmTextField';
import OmSelect from '../ui/OmSelect';
import OmSelectOption from '../ui/OmSelectOption';
import OmPagination from '../ui/OmPagination';
import React from 'react';

// Memoized UserRow component to prevent unnecessary re-renders
const UserRow = React.memo(({
  user,
  actionLoading,
  onEdit,
  onDelete
}) => {
  return (
    <tr className={`hover:bg-silver-mist/50 transition-colors ${!user.is_active ? 'bg-silver-mist/30' : ''}`}>
      <td className="px-6 py-6 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-12 w-12">
            <div className="h-12 w-12 rounded-full bg-fresh-leaf text-crystal-white flex items-center justify-center font-semibold text-lg shadow-sm">
              {user.username.charAt(0).toUpperCase()}
            </div>
          </div>
          <div className="ml-4">
            <div className="text-base font-semibold text-deep-jungle">{user.username}</div>
            <div className="text-sm text-steel">{user.email}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-6 whitespace-nowrap">
        <omk-tag type={user.is_active ? 'positive' : 'danger'}>
          {user.is_active ? 'Active' : 'Inactive'}
        </omk-tag>
      </td>
      <td className="px-6 py-6 whitespace-nowrap">
        <div className="flex flex-wrap gap-2">
          {user.userRoles && user.userRoles.map(ur => (
            <omk-tag key={ur.role.name} type="information">
              {ur.role.name}
            </omk-tag>
          ))}
        </div>
      </td>
      <td className="px-6 py-6 whitespace-nowrap text-sm text-steel">
        {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
      </td>
      <td className="px-6 py-6 whitespace-nowrap text-right">
        <div className="flex justify-end space-x-3">
          <OmButton
            onClick={() => onEdit(user)}
            disabled={actionLoading[`edit-${user.id}`]}
            buttonType="filled-button"
            buttonSize="small"
          >
            {actionLoading[`edit-${user.id}`] ? '...' : 'Edit'}
          </OmButton>
          <OmButton
            onClick={() => onDelete(user)}
            disabled={actionLoading[`delete-${user.id}`]}
            buttonType="outlined-button"
            buttonSize="small"
          >
            {actionLoading[`delete-${user.id}`] ? '...' : 'Delete'}
          </OmButton>
        </div>
      </td>
    </tr>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.user.id === nextProps.user.id &&
    prevProps.user.username === nextProps.user.username &&
    prevProps.user.email === nextProps.user.email &&
    prevProps.user.is_active === nextProps.user.is_active &&
    prevProps.user.lastLogin === nextProps.user.lastLogin &&
    JSON.stringify(prevProps.user.userRoles) === JSON.stringify(nextProps.user.userRoles) &&
    prevProps.actionLoading[`edit-${prevProps.user.id}`] === nextProps.actionLoading[`edit-${nextProps.user.id}`] &&
    prevProps.actionLoading[`delete-${prevProps.user.id}`] === nextProps.actionLoading[`delete-${nextProps.user.id}`]
  );
});

UserRow.displayName = 'UserRow';

function UserList({ onCreateNew, onEdit, onDelete, initialLoad = false, currentPage = 1, itemsPerPage = 10, pagination = {}, onPageChange, onItemsPerPageChange }) {
  // Use specific selectors instead of destructuring with robust type checking
  const usersFromState = useSelector(selectUsers);
  const users = Array.isArray(usersFromState) ? usersFromState : [];
  const isLoading = useSelector(selectUsersLoading);
  const isError = useSelector(selectUsersError);
  const message = useSelector(selectUsersMessage);

  const [actionLoading, setActionLoading] = useState({});
  
  // Configure and use the list filters hook
  const {
    filteredItems: filteredUsers,
    searchTerm,
    setSearchTerm,
    roleFilter,
    setRoleFilter,
    statusFilter,
    setStatusFilter,
    totalItems,
    filteredCount
  } = useListFilters(users, {
    searchFields: ['username', 'email'],
    roleFilter: {
      enabled: true,
      field: 'userRoles',
      roleNamePath: 'role.name'
    },
    statusFilter: {
      enabled: true,
      field: 'is_active',
      activeValue: true,
      inactiveValue: false
    }
  });
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle user actions with loading states
  const handleEdit = (user) => {
    setActionLoading(prev => ({ ...prev, [`edit-${user.id}`]: true }));
    // Simulate brief loading for better UX
    setTimeout(() => {
      setActionLoading(prev => ({ ...prev, [`edit-${user.id}`]: false }));
      onEdit(user);
    }, 300);
  };

  const handleDelete = (user) => {
    setActionLoading(prev => ({ ...prev, [`delete-${user.id}`]: true }));
    // Simulate brief loading for better UX
    setTimeout(() => {
      setActionLoading(prev => ({ ...prev, [`delete-${user.id}`]: false }));
      onDelete(user);
    }, 300);
  };
  
  // Get unique roles from all users
  const allRoles = [...new Set(
    users.flatMap(user => 
      user.userRoles ? user.userRoles.map(ur => ur.role.name) : []
    )
  )];

  // Show skeleton loading on initial load OR when users array is empty and loading
  if ((initialLoad && isLoading) || (isLoading && users.length === 0)) {
    return <UserListSkeleton />;
  }
  
  return (
    <div className="bg-crystal-white min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Header Section */}
          <div className="border-b border-silver-mist pb-6">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
              <div className="space-y-2">
                <h1 className="text-3xl font-display font-bold text-deep-jungle">User Management</h1>
                <p className="text-steel text-sm">Manage user accounts, roles, and permissions</p>
              </div>
              
              <OmButton
                onClick={onCreateNew}
                disabled={isLoading && users.length === 0}
                buttonType="filled-button"
                buttonSize="medium"
                leadingIcon="add"
              >
                {isLoading && users.length === 0 ? 'Loading...' : 'Create New User'}
              </OmButton>
            </div>
          </div>
          
          {/* Search and Filters Section */}
          <div className="card mb-8" style={{ borderRadius: '0.5rem' }}>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Search Input */}
                <div className="space-y-2">
                  <div style={{
                    '--omk-text-field-container-color': '#ffffff',
                    '--omk-text-field-label-text-color': '#006250',
                    '--omk-text-field-input-text-color': '#334155',
                    '--omk-text-field-outline-color': 'rgba(86, 163, 83, 0.3)',
                    '--omk-text-field-focus-outline-color': '#56a353',
                    '--omk-text-field-caret-color': '#56a353'
                  }}>
                    <OmTextField
                      label="Search Users"
                      placeholder="Search by username or email..."
                      value={searchTerm}
                      onChange={(e) => handleSearchChange(e)}
                      disabled={isLoading && users.length === 0}
                      type="text"
                    />
                  </div>
                </div>
              
                {/* Role Filter */}
                <div className="space-y-2">
                  <div style={{
                    '--omk-select-text-field-container-color': '#ffffff',
                    '--omk-select-text-field-label-text-color': '#006250',
                    '--omk-select-text-field-input-text-color': '#334155',
                    '--omk-select-text-field-outline-color': 'rgba(86, 163, 83, 0.3)',
                    '--omk-select-text-field-focus-outline-color': '#56a353'
                  }}>
                    <OmSelect
                      label="Filter by Role"
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      disabled={isLoading && users.length === 0}
                    >
                      <OmSelectOption value="">All Roles</OmSelectOption>
                      {allRoles.map(role => (
                        <OmSelectOption key={role} value={role}>{role}</OmSelectOption>
                      ))}
                    </OmSelect>
                  </div>
                </div>
              
                {/* Status Filter */}
                <div className="space-y-2">
                  <div style={{
                    '--omk-select-text-field-container-color': '#ffffff',
                    '--omk-select-text-field-label-text-color': '#006250',
                    '--omk-select-text-field-input-text-color': '#334155',
                    '--omk-select-text-field-outline-color': 'rgba(86, 163, 83, 0.3)',
                    '--omk-select-text-field-focus-outline-color': '#56a353'
                  }}>
                    <OmSelect
                      label="Filter by Status"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      disabled={isLoading && users.length === 0}
                    >
                      <OmSelectOption value="all">All Status</OmSelectOption>
                      <OmSelectOption value="active">Active</OmSelectOption>
                      <OmSelectOption value="inactive">Inactive</OmSelectOption>
                    </OmSelect>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Results Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-1">
            <div className="text-sm text-steel">
              {pagination.total > 0 ? (
                <>
                  Showing <span className="font-medium text-graphite">{filteredCount}</span> of <span className="font-medium text-graphite">{pagination.total}</span> users
                  {isError && (
                    <span className="ml-2 text-error font-medium">
                      • Error: {message}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-steel">
                  {isError ? `Error loading users: ${message}` : 'No users available'}
                </span>
              )}
            </div>
            
            {/* Loading indicator for refresh */}
            {isLoading && users.length > 0 && (
              <div className="flex items-center space-x-2 text-sm text-steel">
                <div className="loading-spinner w-4 h-4"></div>
                <span>Refreshing...</span>
              </div>
            )}
          </div>
          
          {/* Users Table */}
          <div className="bg-crystal-white rounded-lg shadow-sm border border-silver-mist/20 overflow-hidden">
            <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-silver-mist/50 scrollbar-track-crystal-white hover:scrollbar-thumb-silver-mist/70">
              <table className="min-w-full divide-y divide-silver-mist/30 lg:min-w-0 w-full">
                <thead className="bg-deep-jungle">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-crystal-white uppercase tracking-wider">
                      User Information
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-crystal-white uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-crystal-white uppercase tracking-wider">
                      Roles
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-crystal-white uppercase tracking-wider">
                      Last Login
                    </th>
                    <th scope="col" className="px-6 py-4 text-right text-xs font-medium text-crystal-white uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-crystal-white divide-y divide-silver-mist/20">
                  {totalItems > 0 && filteredCount > 0 ? (
                    filteredUsers.map(user => {
                      // Console log removed for production
                       
                      
                      return (
                        <UserRow 
                          key={user.id}
                          user={user}
                          actionLoading={actionLoading}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                        />
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center max-w-md mx-auto">
                          {isError ? (
                            <>
                              <div className="w-16 h-16 bg-error/10 border-2 border-error/20 flex items-center justify-center mb-4">
                                <svg className="h-8 w-8 text-error" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                </svg>
                              </div>
                              <h3 className="text-lg font-semibold text-deep-jungle mb-2">Failed to load users</h3>
                              <p className="text-steel text-center">{message}</p>
                            </>
                          ) : (
                            <>
                              <div className="w-16 h-16 bg-steel/10 border-2 border-steel/20 flex items-center justify-center mb-4">
                                <svg className="h-8 w-8 text-steel" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                                </svg>
                              </div>
                              <h3 className="text-lg font-semibold text-deep-jungle mb-2">
                                {totalItems === 0 ? 'No users found' : 'No users match your filters'}
                              </h3>
                              <p className="text-steel text-center">
                                {totalItems === 0 
                                  ? 'Get started by creating your first user' 
                                  : 'Try adjusting your search or filter criteria'}
                              </p>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        {/* Pagination Controls - Using OmPagination component like Audit Logs */}
        {!isLoading && !isError && pagination.total > 0 && (
          <div className="mt-6 bg-crystal-white rounded-xl shadow-md border border-silver-mist/30 overflow-hidden">
            <div className="px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-b border-silver-mist/20">
              {/* Items per page selector */}
              <div className="flex items-center gap-3">
                <label htmlFor="items-per-page" className="text-sm font-medium text-steel whitespace-nowrap">
                  Show:
                </label>
                <div className="relative">
                  <select
                    id="items-per-page"
                    value={itemsPerPage}
                    onChange={(e) => {
                      onItemsPerPageChange(parseInt(e.target.value));
                      onPageChange(1); // Reset to first page
                    }}
                    className="appearance-none pl-4 pr-10 py-2.5 text-sm font-medium border border-silver-mist/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-fresh-leaf focus:border-fresh-leaf bg-crystal-white text-deep-jungle transition-all duration-200 hover:border-fresh-leaf/70 hover:shadow-sm cursor-pointer"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="h-4 w-4 text-steel" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                <span className="text-sm text-steel whitespace-nowrap">per page</span>
              </div>
              
              {/* Page info */}
              <div className="text-sm text-steel">
                Page <span className="font-semibold text-deep-jungle">{pagination.page}</span> of <span className="font-semibold text-deep-jungle">{pagination.totalPages}</span>
              </div>
            </div>
            
            <OmPagination
              currentPage={pagination.page || currentPage}
              totalItems={pagination.total}
              itemsPerPage={itemsPerPage}
              onPageChange={onPageChange}
              showResultsInfo={true}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default UserList;
